import type { SWRConfiguration } from "swr"
import type { GridSortModel, GridFilterItem } from "@mui/x-data-grid"
import type { FormSchemaCandidatesType } from "@/src/features/candidates/components/candidatesForm"

import useSWR from "swr"
import axios from "axios"
import dayjs from "dayjs"
import { useMemo } from "react"
import { fetcher, apiUrls, httpClient as axiosInstance } from "@/src/api"

import type { ApiResponse } from "../types/apiResponse"
import type { LabelValuePair } from "../types/labelValuePair"
import type { SpecialTags, CandidateEvaluationModel } from "../types/leadsEvaluationModel"
import {
    type CandidatesModel,
    type CandidateRoundStatus,
    type CandidateTableFilters,
    type CandidateRoundsModel,
    type CandidateNotePostResponse,
} from "../types/candidatesModel"
import { JobRoundModel } from "../types/roundModel"

// ----------------------------------------------------------------------

export const swrOptions: SWRConfiguration = {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    errorRetryCount: 2,
    dedupingInterval: 2000, // Prevent excessive refetches within 2 seconds
}

// ----------------------------------------------------------------------

type LeadsData = {
    data: {
        content: CandidatesModel[]
        totalElements: number | undefined
        leadCountPerStatus: { [key: string]: number }
        inProcessCount: number
        rejectedCount: number
    }
}

export const tableFiltersToJson = (
    search: string,
    filterOptions: GridFilterItem[],
    extraFilters: { [key: string]: dayjs.Dayjs | LabelValuePair<string>[] | string[] | boolean | undefined } | {}
) => {
    const allFilters: any = {}

    if (search) allFilters.search = search

    filterOptions.forEach((filter) => {
        if (filter.value) allFilters[filter.field] = Array.isArray(filter.value) ? filter.value.join(",") : filter.value
    })

    Object.entries(extraFilters).forEach((item) => {
        let value
        if (item[0] === "createdDate" && Array.isArray(item[1])) {
            const [createdDateStart, createdDateEnd] = item[1]
            delete allFilters.createdDate
            if (createdDateStart)
                allFilters.createdDateStart = dayjs(createdDateStart as string)
                    .startOf("day")
                    .toISOString()
            if (createdDateEnd)
                allFilters.createdDateEnd = dayjs(createdDateEnd as string)
                    .endOf("day")
                    .toISOString()
        } else if (item[0] === "callbackDate" && Array.isArray(item[1])) {
            const [callbackDateStart, callbackDateEnd] = item[1]
            delete allFilters.callbackDate
            if (callbackDateStart)
                allFilters.callbackDateStart = dayjs(callbackDateStart as string)
                    .startOf("day")
                    .toISOString()
            if (callbackDateEnd)
                allFilters.callbackDateEnd = dayjs(callbackDateEnd as string)
                    .endOf("day")
                    .toISOString()
        } else if (Array.isArray(item[1])) {
            value = item[1].map((e) => (e as any).value ?? e).join(",")
        } else {
            value = item[1]
        }
        if (dayjs.isDayjs(value)) value = dayjs(value).toISOString()
        if (value !== undefined && value !== null && value !== "") allFilters[item[0]] = value
    })

    return allFilters
}

const convertDateFilterToIso = (value: dayjs.Dayjs | null, isStart: boolean) => {
    if (!value) return null
    if (isStart) {
        value = value.set("hour", 0).set("minute", 0).set("second", 0).set("millisecond", 0)
    } else {
        value = value.set("hour", 23).set("minute", 59).set("second", 59).set("millisecond", 999)
    }
    return value.toISOString()
}

export const getTableFilterString = (
    sortModel: GridSortModel,
    search: string,
    filterOptions: GridFilterItem[],
    extraFilters: {
        [key: string]:
            | [dayjs.Dayjs | null, dayjs.Dayjs | null]
            | LabelValuePair<string>[]
            | string[]
            | boolean
            | undefined
    }
) => {
    const sortQuery = sortModel
        .map((sortRule) => {
            const field = sortRule.field === "matchScoreResult" ? "matchScore" : sortRule.field
            return `${field},${sortRule.sort}`
        })
        .join("&")
    let filters = filterOptions
        .filter((filterOption) => filterOption.value)
        .map((filterOption) => `${filterOption.field}=${filterOption.value}`)
        .join("&")

    const extraFilterValues: string[] = []

    Object.entries(extraFilters).forEach((filterEntry) => {
        let value
        if (Array.isArray(filterEntry[1])) {
            // Date filters are sent in the form of `createdDateStart=2025-04-30T18:30:00.000Z&createdDateEnd=2025-04-30T18:30:00.000Z`
            if (dayjs.isDayjs(filterEntry[1][0]) || dayjs.isDayjs(filterEntry[1][1])) {
                const startDate = dayjs.isDayjs(filterEntry[1][0]) ? dayjs(filterEntry[1][0]) : null
                const endDate = dayjs.isDayjs(filterEntry[1][1]) ? dayjs(filterEntry[1][1]) : null
                if (startDate) {
                    extraFilterValues.push(`${filterEntry[0]}Start=${convertDateFilterToIso(startDate, true)}`)
                }
                if (endDate) {
                    extraFilterValues.push(`${filterEntry[0]}End=${convertDateFilterToIso(endDate, false)}`)
                }
            } else {
                value = filterEntry[1].map((filterValue) => (filterValue as any).value ?? filterValue).join(",")
                if (value) extraFilterValues.push(`${filterEntry[0]}=${value}`)
            }
        } else {
            value = filterEntry[1]
            if (value !== undefined && value !== null) extraFilterValues.push(`${filterEntry[0]}=${value}`)
        }
    })
    if (extraFilterValues.length) {
        filters = filters ? `${filters}&${extraFilterValues.join("&")}` : extraFilterValues.join("&")
    }

    return `${sortQuery ? `&sort=${sortQuery}` : ""}${search ? `&search=${search}` : ""}${filters ? `&${filters}` : ""}`
}

/**
 * Fetches candidates for a job/round. `extraFilters` is serialized by {@link getTableFilterString}:
 * string[] fields (e.g. `aiEvaluationStatus`, `cefrScore`) become `name=a,b,c` query params.
 */
export function useGetLeads(
    page: number,
    size: number,
    sortModel: GridSortModel,
    search: string,
    jobId: string,
    currentRoundId: string | undefined,
    extraFilters: CandidateTableFilters
) {
    const url = jobId ? apiUrls.CANDIDATES_LIST() : null
    const filterString = getTableFilterString(sortModel, search, [], extraFilters)

    const currentRoundIdQuery = currentRoundId ? `&currentRoundId=${currentRoundId}` : ""

    const { data, isLoading, error, isValidating, mutate } = useSWR<LeadsData>(
        url
            ? `${url}?page=${page}&size=${size}&jobId=${jobId}${currentRoundIdQuery}${filterString}&sort=createdDate,DESC`
            : null,
        fetcher,
        swrOptions
    )

    const memoizedValue = useMemo(() => {
        const l = (data?.data?.content || []).map(leadsMapper)
        return {
            leads: l,
            leadsCount: data?.data?.totalElements,
            leadsLoading: isLoading || isValidating,
            tabData: { inProcess: data?.data?.inProcessCount, rejected: data?.data?.rejectedCount },
            leadsError: error,
            leadsEmpty: !isLoading && !data?.data?.content.length,
            leadsMutate: mutate,
        }
    }, [data, error, isLoading, isValidating, mutate])

    return memoizedValue
}

// ----------------------------------------------------------------------

type LeadData = {
    data: {
        candidate: CandidatesModel
        candidateRounds: CandidateRoundsModel[]
    }
}

const findRoundById = (roundId: string | undefined, data: LeadData | undefined) => {
    if (!roundId || !data) return undefined
    return data?.data?.candidateRounds.find((round) => {
        return round.roundId === roundId
    })
}

export function useGetLead({ leadId, page = 0, size }: { leadId: string | undefined; page: number; size: number }) {
    const url = leadId
        ? [apiUrls.CANDIDATE_DETAILS(leadId), { params: { page, size, sort: "createdDate%2Cdesc" } }]
        : ""

    const { data, isLoading, error, isValidating } = useSWR<LeadData>(url, fetcher, swrOptions)

    const memoizedValue = useMemo(
        () => ({
            lead: {
                ...leadsMapper(data?.data?.candidate),
                // aiEvaluationSummary: findRoundById(data?.data?.candidate?.currentRoundId, data)?.aiEvaluationSummary,
                aiEvaluationSummary: data?.data?.candidate?.aiEvaluationSummary,
                roundsData: data?.data?.candidateRounds,
                currentRoundStatus:
                    data?.data?.candidate?.currentRoundStatus ??
                    findRoundById(data?.data?.candidate?.currentRoundId, data)?.aiRoundStatus,
                currentRoundFitmentScore: data?.data?.candidate?.currentRoundFitmentScore,
            },
            leadLoading: isLoading,
            leadError: error,
            leadValidating: isValidating,
        }),
        [data, error, isLoading, isValidating]
    )

    return memoizedValue
}

const mapSpecialTags = (specialTags: SpecialTags | undefined) => {
    if (!specialTags) return undefined

    return {
        positiveMarkers: specialTags.positiveMarkers?.filter((marker) => !!marker.tag?.length),
        averageMarkers: specialTags.averageMarkers?.filter((marker) => !!marker.tag?.length),
        missingMarkers: specialTags.missingMarkers?.filter((marker) => !!marker.tag?.length),
        negativeMarkers: specialTags.negativeMarkers?.filter((marker) => !!marker.tag?.length),
        redFlagMarkers: specialTags.redFlagMarkers.filter((marker) => !!marker.tag?.length),
    }
}

const leadsMapper = (data: any | undefined): CandidatesModel | undefined => {
    if (!data) return undefined

    return {
        ...data,
    }
}

const updateLeadDetails = (
    data: Partial<CandidatesModel> & {
        id: string
    }
) => {
    return axiosInstance.patch(apiUrls.CANDIDATE_STATUS_UPDATE(), {
        leadId: data.id,
        status: (data as any).status,
        subStatus: (data as any).subStatus,
        comment: (data as any).comments,
        resumeLink: (data as any).resumeLink,
        callbackDate: (data as any).callbackDate ? dayjs((data as any).callbackDate).format() : undefined,
        assignedTo: (data as any).assignedTo?.value,
        reason: (data as any).reason,
    })
}

/** Workspace manual candidate create (matches bulk upload: jobId + roundId required). */
export type CandidateCreateApiPayload = {
    jobId: string
    roundId: string
    name: string
    phoneNumber: string
    email: string
    resumeLink: string
}

export const buildCandidateCreatePayload = (
    payload: FormSchemaCandidatesType & { jobId?: string; roundId?: string }
): CandidateCreateApiPayload | null => {
    const jobId = payload.jobId ?? payload.jobDetails?.id ?? ""
    const roundId = payload.roundId ?? ""
    if (!jobId.trim() || !roundId.trim()) return null
    return {
        jobId: jobId.trim(),
        roundId: roundId.trim(),
        name: payload.name ?? "",
        phoneNumber: payload.phoneNumber ?? "",
        email: payload.email ?? "",
        resumeLink: payload.resumeLink ?? "",
    }
}

export const updateLeadsForm = (
    payload: FormSchemaCandidatesType & { jobId?: string; roundId?: string }
): Promise<ApiResponse<string>> => {
    const body = buildCandidateCreatePayload(payload)
    if (!body) {
        return Promise.resolve({
            status: "error",
            errorMessage: "Job and round are required to add a candidate.",
        })
    }
    return axiosInstance
        .post(apiUrls.CANDIDATE_CREATE(), body)
        .then((resp): ApiResponse<string> => {
            return {
                status: "success",
                data: resp?.data?.data?.aiMatchStatus,
            }
        })
        .catch((e): ApiResponse<string> => {
            console.log(e)
            return {
                status: "error",
                errorMessage: e.status,
            }
        })
}

export const updateLeadsFormPublic = (payload: FormSchemaCandidatesType, token: string) => {
    return axiosInstance.post(apiUrls.CANDIDATES_PUBLIC_CREATE, payload, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    })
}

/**
 * @param candidateHash — Partner/ATS walk-in: stored interview hash. Non-ATS public apply: `null` (key still sent for API contract).
 */
export const updateLeadsFormPublicV2 = (
    payload: FormSchemaCandidatesType,
    token: string,
    candidateHash: string | null
) => {
    // Transform payload to match API requirements
    // The API expects jobId but the payload contains mandateId
    const { mandateId, ...restPayload } = payload as any
    const transformedPayload = {
        ...restPayload,
        jobId: mandateId || payload.jobDetails?.id,
        candidateHash,
    }

    return axiosInstance.post(apiUrls.CANDIDATES_PUBLIC_CREATE_V2(), transformedPayload, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    })
}

export const editLeadsForm = (payload: FormSchemaCandidatesType, id: string) => {
    return axiosInstance.put(`${apiUrls.CANDIDATE_UPDATE(id)}`, payload)
}

export const requestPublicInterviewPhoneCall = async (
    participantId: string,
    token: string
): Promise<ApiResponse<string>> => {
    return axiosInstance
        .post(apiUrls.CANDIDATE_PUBLIC_PHONE_CALL(participantId), null, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
        .then((resp): ApiResponse<string> => {
            return {
                status: "success",
                data: resp?.data?.data,
            }
        })
        .catch((e): ApiResponse<string> => {
            console.log(e)
            return {
                status: "error",
                errorMessage: e.status,
            }
        })
}

export type MoveToNextRoundOptions = {
    /**
     * Only for rejected candidates (`NOT_MATCHED`) — reason from the move modal.
     * Sent as `note` in the JSON body. Omit for normal moves (no `note` in payload).
     */
    rejectedMoveNote?: string
}

export const moveToNextRound = async (
    candidateId: string,
    options?: MoveToNextRoundOptions
): Promise<ApiResponse<void>> => {
    const payload: { notes?: string } = {}
    if (options?.rejectedMoveNote != null && options.rejectedMoveNote.trim() !== "") {
        payload.notes = options.rejectedMoveNote.trim()
    }
    return axiosInstance
        .post(apiUrls.CANDIDATE_MOVE_NEXT_ROUND(candidateId), payload)
        .then((resp): ApiResponse<void> => {
            return {
                status: "success",
                data: resp?.data?.data,
            }
        })
        .catch((e): ApiResponse<void> => {
            console.log(e)
            return {
                status: "error",
                errorMessage: e.status,
            }
        })
}

export const rejectCandidate = async (candidateId: string): Promise<ApiResponse<void>> => {
    return axiosInstance
        .post(apiUrls.CANDIDATE_REJECT(candidateId))
        .then((resp): ApiResponse<void> => {
            return {
                status: "success",
                data: resp?.data?.data,
            }
        })
        .catch((e): ApiResponse<void> => {
            console.log(e)
            return {
                status: "error",
                errorMessage: e.status,
            }
        })
}

export const reAttemptRound = async (candidateId: string): Promise<ApiResponse<void>> => {
    return axiosInstance
        .post(apiUrls.CANDIDATE_REATTEMPT_ROUND(candidateId))
        .then((resp): ApiResponse<void> => {
            return {
                status: "success",
                data: resp?.data?.data,
            }
        })
        .catch((e): ApiResponse<void> => {
            console.log(e)
            return {
                status: "error",
                errorMessage: e.status,
            }
        })
}

export const triggerReevaluation = async (candidateId: string, roundId: string): Promise<ApiResponse<void>> => {
    return axiosInstance
        .post(apiUrls.CANDIDATE_REEVALUATE(), {
            candidateId,
            roundId,
        })
        .then((resp): ApiResponse<void> => {
            return {
                status: "success",
                data: resp?.data?.data,
            }
        })
        .catch((e): ApiResponse<void> => {
            console.log(e)
            return {
                status: "error",
                errorMessage: e.status,
            }
        })
}

export const publishJob = async (jobId: string): Promise<ApiResponse<void>> => {
    return axiosInstance
        .post(apiUrls.JOB_PUBLISH(jobId))
        .then((resp): ApiResponse<void> => {
            return {
                status: "success",
                data: resp?.data?.data,
            }
        })
        .catch((e): ApiResponse<void> => {
            console.log(e)
            return {
                status: "error",
                errorMessage: e.status,
            }
        })
}

/** Export row: standard fields plus optional `customExportColumns` (header label → cell value). */
export type ExportCandidateItem = {
    candidateName: string
    candidateNumber: string
    candidateEmailId: string
    aiTag: string
    cefrLevel: string
    cefrScore: string
    overallSummary: string
    createdDate?: string
    scheduleStatus?: string
    notScheduledReason?: string
    scheduledDate?: string
    scheduledTime?: string
    rescheduleCount?: number
    customExportColumns?: Record<string, string | null>
}

export const exportCandidates = async (jobId: string): Promise<ApiResponse<ExportCandidateItem[]>> => {
    return axiosInstance
        .get(apiUrls.EXPORT_CANDIDATES(jobId))
        .then((resp): ApiResponse<ExportCandidateItem[]> => {
            const data = resp?.data?.data
            const list = data?.candidates && Array.isArray(data.candidates) ? data.candidates : []
            return {
                status: "success",
                data: list as ExportCandidateItem[],
            }
        })
        .catch((e): ApiResponse<ExportCandidateItem[]> => {
            console.log(e)
            return {
                status: "error",
                errorMessage: e.status,
            }
        })
}

/** Response shape for count-by-status API: aiEvaluationStatus and cefrScore counts. */
export type CandidatesCountByStatusData = {
    aiEvaluationStatus?: Record<string, number>
    cefrScore?: Record<string, number>
}

/** Count candidates by AI evaluation status and CEFR score. */
export async function getCandidatesCountByStatus(jobId: string): Promise<ApiResponse<CandidatesCountByStatusData>> {
    try {
        const resp = await axiosInstance.get(apiUrls.CANDIDATES_COUNT_BY_STATUS(jobId))
        const data = resp?.data?.data ?? {}
        return {
            status: "success",
            data: {
                aiEvaluationStatus: data.aiEvaluationStatus ?? {},
                cefrScore: data.cefrScore ?? {},
            },
        }
    } catch (e: any) {
        return {
            status: "error",
            errorMessage: e?.response?.data?.message ?? e?.message ?? "Failed to fetch count by status",
        }
    }
}

export const addCandidateNote = async (
    candidateId: string,
    note_text: string
): Promise<ApiResponse<CandidateNotePostResponse>> => {
    return axiosInstance
        .post(apiUrls.CANDIDATE_ADD_NOTE(candidateId), { note_text })
        .then((resp): ApiResponse<CandidateNotePostResponse> => {
            return {
                status: "success",
                data: resp?.data?.data as CandidateNotePostResponse,
            }
        })
        .catch((e): ApiResponse<CandidateNotePostResponse> => {
            console.log(e)
            return {
                status: "error",
                errorMessage: e?.response?.data?.message ?? e?.message ?? "Failed to save note",
            }
        })
}

export const retakeRound = async (candidateRoundId: string, note: string): Promise<ApiResponse<void>> => {
    return axiosInstance
        .post(apiUrls.CANDIDATE_RETAKE_ROUND(candidateRoundId), { note })
        .then((resp): ApiResponse<void> => {
            return {
                status: "success",
                data: resp?.data?.data,
            }
        })
        .catch((e: any): ApiResponse<void> => {
            console.log(e)
            return {
                status: "error",
                errorMessage: e?.response?.data?.message ?? e?.message ?? "Failed to retake round",
            }
        })
}

const PDF_POLL_INTERVAL_MS = 2500
const PDF_POLL_MAX_MS = 120_000

export type PdfSignedUrlPayload = {
    workspaceId?: string
    jobId?: string
    candidateId?: string
    privateUrl?: string
    signedUrl?: string
    pdfGeneratedTillRoundId?: string
}

type PdfSignedUrlApiEnvelope = {
    statusCode?: number
    status?: string
    message?: string
    data?: PdfSignedUrlPayload
}

function pdfPollDelay(signal: AbortSignal | undefined): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        const t = window.setTimeout(resolve, PDF_POLL_INTERVAL_MS)
        signal?.addEventListener(
            "abort",
            () => {
                window.clearTimeout(t)
                reject(new DOMException("Aborted", "AbortError"))
            },
            { once: true }
        )
    })
}

/**
 * GET `/workspace/{workspaceId}/pdf/signed-url` — returns payload when the response includes a non-empty `signedUrl`, otherwise `undefined`.
 */
export async function fetchPdfSignedUrl(
    jobId: string,
    candidateId: string,
    options?: { signal?: AbortSignal }
): Promise<PdfSignedUrlPayload | undefined> {
    const res = await axiosInstance.get<PdfSignedUrlApiEnvelope>(apiUrls.PDF_SIGNED_URL(jobId, candidateId), {
        signal: options?.signal,
    })
    const body = res.data
    const statusOk = body?.statusCode === undefined || body?.statusCode === 1000
    const signedUrl = body?.data?.signedUrl
    if (!statusOk || typeof signedUrl !== "string" || !signedUrl.trim()) {
        return undefined
    }
    return body.data
}

export type CandidateProfilePdfRequestParams = {
    jobId: string
    candidateId: string
    roundId: string
}

/**
 * POST `/workspace/{workspaceId}/pdf/generate` — kicks off async candidate profile PDF generation.
 */
export async function requestCandidateProfilePdfGeneration(
    params: CandidateProfilePdfRequestParams,
    options?: { signal?: AbortSignal }
): Promise<void> {
    await axiosInstance.post(apiUrls.PDF_GENERATE(), params, { signal: options?.signal })
}

/**
 * Polls GET `/workspace/{workspaceId}/pdf/signed-url` until `data.signedUrl` is present (after POST `/pdf/generate`).
 */
/* eslint-disable no-await-in-loop -- intentional sequential polling */
export async function pollForPdfSignedUrl(
    jobId: string,
    candidateId: string,
    options?: { signal?: AbortSignal }
): Promise<string | undefined> {
    const deadline = Date.now() + PDF_POLL_MAX_MS
    const signal = options?.signal

    while (Date.now() < deadline) {
        if (signal?.aborted) return undefined
        try {
            const payload = await fetchPdfSignedUrl(jobId, candidateId, { signal })
            const url = payload?.signedUrl
            if (typeof url === "string" && url.trim()) return url.trim()
        } catch (e) {
            if (axios.isCancel(e)) throw e
            if (axios.isAxiosError(e)) {
                const status = e.response?.status
                if (status === 401 || status === 403) throw e
            }
            // Transient errors or PDF not ready yet — keep polling until timeout.
        }

        await pdfPollDelay(signal)
    }
    return undefined
}
/* eslint-enable no-await-in-loop */
